// src/components/WhatsAppQuotaIndicator.tsx
// Indicador de cuota de WhatsApp en el dashboard

import React, { useState, useEffect } from 'react';

// ⚙️ Mostrar el banner de cuota de WhatsApp (Meta).
// Hoy el método por defecto son los recordatorios manuales, así que lo
// dejamos APAGADO para no confundir. Poner en true si se reactiva Meta.
const SHOW_WHATSAPP_QUOTA = false;

interface QuotaData {
  used: number;
  limit: number;
  remaining: number | string;
  percentage: number;
  plan: string;
  planDescription: string;
  blocked: number;
  breakdown: Record<string, number>;
  resetsAt: string;
}

const WhatsAppQuotaIndicator: React.FC = () => {
  const [quota, setQuota] = useState<QuotaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    loadQuota();
  }, []);

  const loadQuota = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/whatsapp/quota`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setQuota(data);
      }
    } catch (err) {
      console.error('Error cargando cuota WhatsApp:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!SHOW_WHATSAPP_QUOTA) return null;
  if (loading || !quota) return null;

  const isUnlimited = quota.limit === 0;
  const percentage = isUnlimited ? 0 : quota.percentage;
  const isLow = percentage >= 80;
  const isCritical = percentage >= 95;

  const daysUntilReset = () => {
    const reset = new Date(quota.resetsAt);
    const now = new Date();
    return Math.ceil((reset.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  const typeLabels: Record<string, string> = {
    confirmation: 'Confirmaciones',
    pending: 'Pendientes',
    reminder: 'Recordatorios',
    professional: 'Notif. profesional'
  };

  return (
    <div 
      onClick={() => setExpanded(!expanded)}
      className={`rounded-xl p-4 cursor-pointer transition-all ${
        isCritical ? 'bg-red-50 border border-red-200' : 
        isLow ? 'bg-yellow-50 border border-yellow-200' : 
        'bg-green-50 border border-green-200'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <svg 
            className={`w-5 h-5 ${
              isCritical ? 'text-red-600' : isLow ? 'text-yellow-600' : 'text-green-600'
            }`} 
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <span className={`text-sm font-semibold ${
            isCritical ? 'text-red-700' : isLow ? 'text-yellow-700' : 'text-green-700'
          }`}>
            WhatsApp
          </span>
        </div>
        <span className={`text-sm font-bold ${
          isCritical ? 'text-red-700' : isLow ? 'text-yellow-700' : 'text-green-700'
        }`}>
          {isUnlimited ? (
            <>{quota.used} enviados</>
          ) : (
            <>{quota.remaining}/{quota.limit}</>
          )}
        </span>
      </div>

      {/* Progress bar */}
      {!isUnlimited && (
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${
              isCritical ? 'bg-red-500' : isLow ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}

      <p className="text-xs text-gray-600 mt-2">
        {isUnlimited ? (
          <span>Plan ilimitado • {quota.used} mensajes este mes</span>
        ) : isCritical ? (
          <span className="text-red-600 font-semibold">
            ⚠️ Quedan {quota.remaining} mensajes. Se reinicia en {daysUntilReset()} días.
          </span>
        ) : isLow ? (
          <span className="text-yellow-600 font-semibold">
            ⚠️ Te quedan {quota.remaining} mensajes este mes.
          </span>
        ) : (
          <span>
            {quota.remaining} mensajes restantes • Se reinicia en {daysUntilReset()} días
          </span>
        )}
      </p>

      {/* Detalle expandido */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-200/50 space-y-2">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Plan: {quota.planDescription}</span>
            <span className="px-2 py-0.5 bg-white/70 rounded text-gray-600 font-medium">{quota.plan}</span>
          </div>
          
          {Object.keys(quota.breakdown).length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-600">Desglose este mes:</p>
              {Object.entries(quota.breakdown).map(([type, count]) => (
                <div key={type} className="flex justify-between text-xs text-gray-500">
                  <span>{typeLabels[type] || type}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          )}
          
          {quota.blocked > 0 && (
            <div className="text-xs text-red-500 font-medium">
              ⛔ {quota.blocked} mensajes bloqueados por cuota este mes
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppQuotaIndicator;